/*
  Desktop mega menu — opening, closing, keyboard, and the panel's height budget.

  Markup in snippets/nav-item.liquid + snippets/mega-menu-panel.liquid, styling in
  assets/mega-menu.css. Loaded from the bottom of sections/header.liquid, the same way the mobile
  drawer loads assets/mobile-menu.js from the bottom of snippets/side-menu.liquid.

  WHAT THIS REPLACES

  assets/common.js:navigationMenu() had twenty lines for the hover case: `mouseover` adds the open
  attribute, `mouseleave` removes it. Three problems that all show up in use:

  - No hover intent. Sweeping the mouse across the nav bar to reach the cart opened and closed
    every panel on the way past.
  - `mouseover` BUBBLES, so every mouse move over any link inside an open panel re-fired the open
    handler.
  - No keyboard at all. In hover mode `.yv-dropdown-detail` is a plain <div> with an `open`
    attribute simulating <details>, so a keyboard user could tab to the trigger and never reach
    the menu under it.

  That function is left in place and still owns the click branch. Its hover branch is now gated on
  `!window.__desktopMegaMenu`, so if this file fails to load the old instant-hover behaviour still
  runs and the menus never become inert.

  THE CONTRACT WITH common.js

  Open state is the `open` ATTRIBUTE plus the `.open` CLASS on `.yv-dropdown-detail`, and nothing
  else. That is already the vocabulary of hideAllMenu(), closeDrawers()'s body-click and Escape
  handlers, removeClasses(), and the `.yv-dropdown-detail[open]` rule in header.min.css. Keeping to
  it means those keep working unchanged and this file never has to know about them.

  All state is read from the DOM on each interaction and every listener is delegated off document,
  because the theme editor replaces the whole header node on shopify:section:load and cached
  element references would point at detached nodes.
*/
(function () {
  'use strict';

  /* assets/common.js checks this to stand down from the hover case. Set before anything else, and
     before DOMContentLoaded fires: both files are deferred, and deferred scripts run in document
     order, ahead of that event. */
  if (window.__desktopMegaMenu) return;
  window.__desktopMegaMenu = true;

  var DISCLOSURE = '.yv-dropdown-detail';
  var TRIGGER = '.dropdown-menu-item';

  /* Long enough that crossing the nav bar to reach the cart opens nothing, short enough that a
     deliberate hover does not feel laggy. */
  var OPEN_DELAY = 110;
  /* Long enough to cross the seam between the trigger and the panel below it, and to forgive a
     mouse that slips out of the panel edge and comes straight back. */
  var CLOSE_DELAY = 180;

  /* Breathing room under the panel so it never sits flush against the bottom of the window. */
  var VIEWPORT_GUTTER = 24;
  /* Below this there is no sensible layout left, so let the panel scroll instead of shrinking to
     nothing. Matches --dmm-min-h in the stylesheet. */
  var MIN_AVAIL = 300;

  var openTimer = null;
  var closeTimer = null;
  var measureFrame = null;

  /* Escape returns focus to the trigger, and focus() dispatches focusin synchronously — which the
     handler below would read as "the user tabbed here" and reopen the panel the Escape just shut.
     Set around the focus() call only; nothing else touches it. */
  var ignoreFocusIn = false;

  function header() {
    return document.querySelector('header');
  }

  /* Only the hover branch is ours. In click mode the disclosure is a real <details>/<summary> and
     common.js already handles it correctly. */
  function isHoverMode() {
    var el = header();
    return !!el && el.getAttribute('data-naviation-open-method') === 'hover';
  }

  /* Hover is only a real input on a fine pointer. The desktop nav is display:none below 992px, but
     a touchscreen laptop is wider than that: without this, a tap meant to follow the link would
     open a panel over the page the tap was navigating to. */
  function hoverCapable() {
    if (typeof window.matchMedia !== 'function') return true;
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  function active() {
    return isHoverMode() && hoverCapable();
  }

  function triggerFor(disclosure) {
    return disclosure ? disclosure.querySelector(TRIGGER) : null;
  }

  /* ------------------------------------------------------------- height budget */

  /* Writes the panel's two measured values.

     --dmm-bleed-r: the distance from the right edge of the panel's centred .container to the right
     edge of the document. assets/mega-menu.css turns it into a negative right margin on the promo
     rail, which is what takes the promo out to the edge of the window.

     documentElement.clientWidth, NOT innerWidth and not the CSS-only `calc(50% - 50vw)` this
     replaces: vw and innerWidth both count the classic scrollbar, so on Windows either overshoots
     by half its width and the panel puts a horizontal scrollbar on the page every time it opens.
     clientWidth is the one that excludes it. Floored rather than rounded for the same reason — a
     sub-pixel over is an overflow, a sub-pixel under is invisible.

     --dmm-avail: the panel's real distance to the bottom of the window.

     Measured on `.yv-dropdown-menus-outer` rather than on the panel itself, for two reasons. It
     carries no styling of its own anywhere in the theme, and — the load-bearing one — it is the
     one box in this subtree that nothing animates. The panel's own entrance is a fade today, but
     its contents drop in on the Y axis and the panel itself carried a translateY until recently;
     anchoring here means the number cannot be skewed by whatever the entrance is doing this week.
     Reading the rect of a visibility:hidden element is fine; it is laid out, just not painted.

     This is what makes the CSS fallback in mega-menu.css unnecessary in practice. It is right at
     rest under the announcement bar, right once the sticky header has engaged and the bar has
     scrolled away, right on the transparent-header homepage, and right under browser zoom —
     none of which a fixed vh figure can be. */
  function measure(disclosure) {
    if (!disclosure) return;
    var outer = disclosure.querySelector('.yv-dropdown-menus-outer');
    var body = disclosure.querySelector('.dmm-body');
    if (!body) return;

    var anchor = outer || disclosure.querySelector('.yv-dropdown-menus');
    if (!anchor) return;

    var top = anchor.getBoundingClientRect().top;
    var avail = window.innerHeight - top - VIEWPORT_GUTTER;
    body.style.setProperty('--dmm-avail', Math.max(MIN_AVAIL, Math.round(avail)) + 'px');

    /* .container in the fullwidth case, and a padding-stripped box in the non-fullwidth one — which
       never has a promo rail, so the value it produces there is written and never read. */
    var container = body.parentNode;
    if (container && container.getBoundingClientRect) {
      var slack = document.documentElement.clientWidth - container.getBoundingClientRect().right;
      body.style.setProperty('--dmm-bleed-r', Math.max(0, Math.floor(slack)) + 'px');
    }
  }

  /* ---------------------------------------------------------- entrance sequence */

  /* Writes --dmm-i: the entrance sweep index, which assets/mega-menu.css turns into an
     animation-delay so the panel arrives column by column instead of all at once.

     Read off layout rather than emitted by Liquid, because the unit is the COLUMN and which
     column a group lands in is a layout fact, not a markup one. .dmm-links is a multicol
     container with column-fill: balance, so a five-group menu in four columns puts two groups in
     one of them (Furniture does exactly this), and a narrow window drops the rendered count below
     --dmm-cols on its own. Numbering by source order stagger those two apart and the column
     fills in two beats, which is the thing that reads as scattered rather than as a sweep.

     Groups sharing a column share an index and move together. Same pass covers a flat menu, where
     the fragmented <li> are the things distributed across columns.

     Called from openNow while the panel is still visibility:hidden. That is fine and is the same
     assumption measure() makes above: hidden elements are laid out, just not painted. It has to
     be before the open attribute goes on, or the first frame of the animation runs against
     whatever indices the last open left behind.

     Not re-run on resize. The panel closes on mouseleave, so a window resized mid-hover is not a
     real path; if it ever becomes one, call this from measureOpen as well. */
  function sequence(disclosure) {
    var panel = disclosure.querySelector('.dmm-panel');
    if (!panel) return;

    var items = panel.querySelectorAll('.dmm-group, .dmm-flat > li');
    var step = -1;
    var edge = null;
    var i;

    for (i = 0; i < items.length; i++) {
      /* Rounded, and compared with a 1px tolerance, because sub-pixel column gutters would
         otherwise make every item look like the start of a new column. */
      var left = Math.round(items[i].getBoundingClientRect().left);
      if (edge === null || Math.abs(left - edge) > 1) {
        step += 1;
        edge = left;
      }
      items[i].style.setProperty('--dmm-i', step);
    }

    /* The promo cards and the footer link carry on from the last column rather than restarting,
       so the whole panel is one sweep. Each card is its own beat; there are at most four. */
    var tail = panel.querySelectorAll('.dmm-rail .dmm-card, .dmm-footer');
    for (i = 0; i < tail.length; i++) {
      tail[i].style.setProperty('--dmm-i', step + 1 + i);
    }
  }

  function measureOpen() {
    if (measureFrame) return;
    measureFrame = window.requestAnimationFrame(function () {
      measureFrame = null;
      var open = document.querySelector(DISCLOSURE + '[open]');
      if (open) measure(open);
    });
  }

  /* ------------------------------------------------------------------- open/close */

  function closeNow(disclosure) {
    if (!disclosure) return;
    disclosure.removeAttribute('open');
    disclosure.classList.remove('open');
    var trigger = triggerFor(disclosure);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  function closeAll(except) {
    var open = document.querySelectorAll(DISCLOSURE + '[open]');
    for (var i = 0; i < open.length; i++) {
      if (open[i] !== except) closeNow(open[i]);
    }
  }

  function openNow(disclosure) {
    if (!disclosure) return;
    /* Immediately, with no delay of its own: two panels cross-fading over each other reads as a
       glitch, and the one being left is the one the user has already decided against. */
    closeAll(disclosure);
    measure(disclosure);
    sequence(disclosure);
    disclosure.setAttribute('open', '');
    disclosure.classList.add('open');
    var trigger = triggerFor(disclosure);
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  }

  function clearTimers() {
    if (openTimer) { clearTimeout(openTimer); openTimer = null; }
    if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
  }

  function scheduleOpen(disclosure) {
    clearTimers();
    openTimer = setTimeout(function () {
      openTimer = null;
      openNow(disclosure);
    }, OPEN_DELAY);
  }

  function scheduleClose(disclosure) {
    clearTimers();
    closeTimer = setTimeout(function () {
      closeTimer = null;
      closeNow(disclosure);
    }, CLOSE_DELAY);
  }

  /* ---------------------------------------------------------------- mouse */

  /* mouseenter/mouseleave do not bubble, so they are delegated in the CAPTURE phase — the document
     still sees them on their way down to the target. This is the fix for the old handler's use of
     mouseover, which bubbles and therefore re-fired on every mouse move inside an open panel. */
  document.addEventListener('mouseenter', function (event) {
    if (!active()) return;
    var target = event.target;
    if (!target || !target.classList) return;

    if (target.classList.contains('yv-dropdown-detail')) {
      /* Already open — the pointer has come back from the panel edge. Cancel the pending close
         rather than restarting the open delay, or returning would cost another 110ms. */
      if (target.hasAttribute('open')) {
        clearTimers();
        return;
      }
      scheduleOpen(target);
    }
  }, true);

  document.addEventListener('mouseleave', function (event) {
    if (!active()) return;
    var target = event.target;
    if (!target || !target.classList) return;

    if (target.classList.contains('yv-dropdown-detail')) {
      scheduleClose(target);
      return;
    }

    /* Leaving the nav row entirely is unambiguous — no forgiveness delay, and it also catches the
       case where the pointer exits sideways past the last item. */
    if (target.classList.contains('nav__header')) {
      clearTimers();
      closeAll(null);
    }
  }, true);

  /* ------------------------------------------------------------- keyboard */

  /* Focus opens with NO delay. Hover intent is a mouse concept: a keyboard user who has tabbed to
     the trigger has already made the choice a hover delay is waiting to confirm. */
  document.addEventListener('focusin', function (event) {
    if (!isHoverMode() || ignoreFocusIn) return;
    var disclosure = event.target.closest ? event.target.closest(DISCLOSURE) : null;
    /* Opens only. Closing is focusout's job, and it already covers every keyboard exit: tabbing
       past the last link in a panel fires focusout on that link with the next nav item as
       relatedTarget, which is outside the disclosure.

       This used to also close everything when focus landed anywhere else on the page, which sounds
       harmless and is not: a third-party popup that autofocuses itself (Klaviyo does, a few seconds
       after load) would snap shut a menu the user was reading. */
    if (!disclosure) return;
    if (!disclosure.hasAttribute('open')) {
      clearTimers();
      openNow(disclosure);
    }
  });

  /* This is why the open state is driven from JS rather than a CSS :focus-within rule: a
     :focus-within panel cannot be escaped from, because focusing the trigger in order to leave
     keeps the rule matching. */
  document.addEventListener('focusout', function (event) {
    if (!isHoverMode()) return;
    var disclosure = event.target.closest ? event.target.closest(DISCLOSURE) : null;
    if (!disclosure) return;
    var next = event.relatedTarget;
    if (next && disclosure.contains(next)) return;
    closeNow(disclosure);
  });

  /* Capture phase, so this runs before the jQuery keydown handler bound to <body> in
     closeDrawers(). That handler then finds nothing open and is a harmless no-op — but focus has
     already been returned here, which it would not have done. */
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' && event.keyCode !== 27) return;
    var open = document.querySelector(DISCLOSURE + '[open]');
    if (!open) return;
    clearTimers();
    var trigger = triggerFor(open);
    closeNow(open);
    if (trigger && typeof trigger.focus === 'function') {
      ignoreFocusIn = true;
      trigger.focus();
      ignoreFocusIn = false;
    }
  }, true);

  /* ------------------------------------------------------------ re-measure */

  window.addEventListener('resize', measureOpen);

  /* The sticky header changes height and position as the page scrolls, and the announcement bar
     above it scrolls away entirely — both move the panel's top edge while it is still open. */
  window.addEventListener('scroll', measureOpen, { passive: true });

  /* Editing header settings or blocks re-renders the whole section. There is no per-node setup to
     redo — every listener above is on document and every selector is resolved at event time — but
     a timer left pointing at a node that no longer exists would fire against a detached element. */
  document.addEventListener('shopify:section:load', function () {
    clearTimers();
  });
})();
